<template>
  <div :class="componentClasses">
    <input
      class="stk-file-input"
      type="file"
      ref="fileInput"
      :multiple="multiple"
      :accept="accept"
      @change="handleFileChange"
    />

    <Button class="stk-file-button" :variant="selectedFiles.length ? '': 'primary'" @click="triggerFileSelect">
      Choose {{ label }}
    </Button>

    <div class="stk-file-count">
      <p v-if="selectedFiles.length">
        <strong>{{ countLabel }}</strong> selected
        <span class="stk-file-toggle" @click="showList = !showList">
          {{ showList ? 'Hide' : 'Show' }} list
        </span>
      </p>
      <p v-else>
        No file selected
      </p>
    </div>

    <Badges v-if="selectedFiles.length && showList" gap="2" class="stk-file-list">
      <Badge v-for="(name, index) in fileNames" status="primary" class="stk-file-item">
        {{ name }}
      </Badge>
    </Badges>
  </div>
</template>

<style>
.stk-file {
  flex-grow: 1;
  align-items: center;
  min-width: 16rem;
  gap: 0.5em 1em;
  display: grid;
  grid-template-columns: 16rem 1fr;

  &.is-vertical {
    display: flex;
    flex-direction: column;
    text-align: center;
    width: 100%;

    .stk-file-button {
      max-width: none;
    }
  }
}

.stk-file-button {
  flex-grow: 1;
  max-width: 16rem;
  width: 100%;
}

.stk-file-input {
  pointer-events: none;
  position: absolute;
  visibility: hidden;
}

.stk-file-list {
  flex-grow: 1;
  align-items: center;
  display: flex;
  flex-wrap: wrap;
  gap: 0.5em;
  line-height: 1.25;
  grid-column: 1 / span 2;
}

.stk-file-item {
  background-color: var(--stk-scheme-main);
}

.stk-file-toggle {
  color: inherit;
  cursor: pointer;
  text-decoration: underline;
}
</style>

<script setup>
import { ref, computed } from 'vue';
import Badge from '@stakeui/content/badge/Badge.vue';
import Badges from '@stakeui/content/badge/Badges.vue';
import Button from '@stakeui/form/button/Button.vue';

const props = defineProps({
  multiple: Boolean,
  onChange: Function,
  accept: String,
  variant: String,
});

const emit = defineEmits(['update:modelValue']);

const fileInput = ref(null);
const selectedFiles = ref([]);
const showList = ref(false);

// Handle file selection
const handleFileChange = (event) => {
  const files = Array.from(event.target.files);
  selectedFiles.value = files;

  // Automatically show file list if only one file is selected
  if (files.length === 1) showList.value = true;
  
  emit('update:modelValue', props.multiple ? files : files[0]);
  emit('change', event);

  // Trigger the custom onChange function if provided
  if (typeof props.onChange === 'function') {
    props.onChange(event);
  }
};

// Trigger file selection
const triggerFileSelect = () => {
  fileInput.value.click();
};

const label = computed(() => props.multiple ? "File(s)" : "File");
const countLabel = computed(() => selectedFiles.value.length > 1 ? `${selectedFiles.value.length} files` : `1 file`);
const fileNames = computed(() => selectedFiles.value.map(file => file.name));

const componentClasses = computed(() => [
  'stk-file',
  props.variant ? `is-${props.variant}` : '',
]);
</script>
